// previewOverlay — プレビュー floating panel の純粋関数群.
//
// 旧版 `PreviewCanvas.java` (= `PreviewFrame.java` 内に貼られる小窓) 踏襲:
// キャンバス全体を縮小描画し、現在の表示範囲を黒枠で重ね、クリック/ドラッグで
// 表示位置を更新する.
//
// 2026-09-29 viewport transform 移行 (決定5、modern-phone-shell-2026-07-02.md §6):
// 表示位置の更新先は旧 `canvas wrapper の scrollLeft/scrollTop` から
// `infrastructure/drawing/viewport.ts` の `{tx,ty}` (ViewportOffset) へ置き換えた
// (= scroll コンテナ自体が無くなったため)。この移行に伴い `previewClickToScroll`
// → `previewClickToViewport` に改名し、戻り値も `{scrollLeft,scrollTop}` から
// `ViewportOffset` に変わっている (呼び出し側は仕様変更として更新済み)。
//
// React component (PreviewOverlay.tsx) は DOM / `<canvas>` / `requestAnimation
// Frame` を扱うので vitest (node env, no DOM) では直接 test できない. ここで
// 「サムネイル寸法計算」「表示範囲枠の座標計算」「クリック → viewport 位置変換」
// の 3 つを純粋関数として切り出して単体テストに乗せる. component 側はこれらを
// 組み合わせるだけにして component 自体の test は不要にする.

import {
  clampViewport,
  clampViewportForTransform,
  type ViewPoint,
  type ViewportOffset,
  type ViewTransform,
  viewportCenteredOn,
  visibleCanvasQuad,
} from '../../../infrastructure/drawing/viewport.js';

/**
 * サムネイル領域の最大辺 (px). 旧版は固定 50x74 だったが新版キャンバス
 * (= 1500² など正方形ベース) では小さすぎて表示範囲枠が潰れるため、
 * 240px を上限に取った. ColorWheelPalette (= 280px 幅) より少し狭く、
 * 横幅 ~260px (= 240 + padding) で「描画 canvas を侵さない floating」に収まる.
 */
export const PREVIEW_THUMBNAIL_MAX_EDGE = 240;

// ─── サムネ再生成レート (= 低レートスロットル, feature/preview-perf 2026-06-13) ───
//
// 旧実装はサムネを **毎フレーム (rAF 60fps) 無条件に多段 mipmap 縮小 + per-pixel
// 細線ブースト readback** で焼き直していた (= 変更検知ゼロで 60fps 全面ダウン
// スケール). これがメインスレッド予算を食い、(a) ブラシカーソル (pointermove)
// のカクつき、(b) 表示範囲枠ドラッグの遅延、(c) 描画中の重さ を引き起こす真因
// だった (consult 2026-06-13)。launch (Modern 公開) で「重い」印象を避けるため、
// サムネ再生成を **低レートにスロットル** + **ローカル操作中はサスペンド** +
// **表示範囲枠はサムネと分離** して負荷を最小化する。
//
// サムネ自体の縮小も多段 mipmap + 細線ブースト readback をやめ、**単発 drawImage**
// に置換 (= getImageData/putImageData の高コスト readback パスを撤去)。サムネ上の
// 細線が多少甘くなるのは launch では許容 (ユーザ確定済)。

/** サムネ再生成の上限レート (fps). この間隔より速くは焼き直さない (≈ 160ms 間隔)。 */
export const PREVIEW_THUMB_FPS = 6;
/** サムネ再生成の最小間隔 (ms). `1000 / PREVIEW_THUMB_FPS` を事前計算した定数。 */
export const PREVIEW_THUMB_INTERVAL_MS = 1000 / PREVIEW_THUMB_FPS;

/**
 * サムネを今フレーム再生成すべきか判定する純粋関数 (スロットル + サスペンド).
 *
 * - `suspended === true` (= 自分のストローク中 / パン中) なら常に false。手を止めた
 *   次の機会で 1 回追いつけば良いので、操作中はサムネ `drawImage` を一切走らせない
 *   (= カーソルのフレーム予算を奪わない)。
 * - それ以外は「前回生成から `intervalMs` 以上経過した最初のフレーム」だけ true。
 *   ホバー/静止中でもこの低レートなので毎フレーム全面ダウンスケールしない。
 * - `lastAt === null` (= まだ一度も生成していない) なら即 true (= 初回は待たない)。
 *
 * @param now 現在時刻 (ms, `performance.now()` 等).
 * @param lastAt 前回サムネ生成時刻 (ms) / 未生成なら null.
 * @param intervalMs 最小間隔 (既定 `PREVIEW_THUMB_INTERVAL_MS`).
 * @param suspended ローカル操作中 (= 再生成を止める) か.
 */
export const shouldRegenThumbnail = (
  now: number,
  lastAt: number | null,
  intervalMs: number = PREVIEW_THUMB_INTERVAL_MS,
  suspended = false,
): boolean => {
  if (suspended) return false;
  if (lastAt === null) return true;
  return now - lastAt >= intervalMs;
};

/**
 * 「自分が描いている / 表示位置を動かしている」= サムネ系の処理 (プレビューの再生成・
 * レイヤーのサムネの貼り付け) を後回しにする判定。`shouldRegenThumbnail` の `suspended` に
 * 渡すものと、レイヤーのサムネ (`useLayerThumbnails`) が同じ式を共有する。
 */
export const isLocalInteractionActive = (strokeActive: boolean, panActive: boolean): boolean =>
  strokeActive || panActive;

/**
 * サムネイル領域の寸法 (px) と、main canvas との縮尺.
 *
 * `scale` は「main canvas 1px がサムネ何 px に対応するか」(= サムネ ÷ main).
 * クリック座標変換でも viewport 枠計算でも共通で使う.
 */
export interface PreviewThumbnailSize {
  readonly width: number;
  readonly height: number;
  readonly scale: number;
}

/**
 * canvas 寸法からサムネイル寸法を算出する純粋関数.
 *
 * - アスペクト比は維持. 長辺が `PREVIEW_THUMBNAIL_MAX_EDGE` に張り付く.
 * - サムネ 1px 未満になる極端な縦横比でも最低 1px を保証 (defensive、現在の
 *   canvas preset では発生しないが、将来「横長 4000x100」みたいな preset が
 *   足された場合に NaN / 0 が出ないよう).
 */
// ─── サムネ最大辺のドラッグリサイズ (= H, preview-resize.md §1, 2026-06-08) ───
//
// 旧版 `PreviewFrame.java` は JDialog で OS 標準の resize gizmo を持っていたが、
// 新版は `position: fixed` の DIV なので自前で右下グリップを出して
// `pointerdown -> move` で **サムネ最大辺 (maxEdge) スカラ 1 本** を更新する.
// アスペクト固定で `computePreviewThumbnailSize(maxEdge)` に流すだけなので、
// チャット detach 窓の 2D `nextSizeFromResize` ではなくスカラ版を用意する
// (= padding 換算が混ざらず綺麗). pure helper として外出しした理由は
// `floatingChatPopup.ts` の resize helper と同じ (= vitest node env でテスト
// するため + clamp ロジックを 1 箇所に集約するため).

/** サムネ最大辺の最小値 (px). これ以下では表示範囲枠が潰れて視認できない. */
export const PREVIEW_MIN_EDGE = 160;
/** サムネ最大辺の最大値 (px). 拡大方向が主目的 (= 既定 240 → 640 まで). */
export const PREVIEW_MAX_EDGE = 640;
/**
 * サムネ最大辺の既定値 (px). 永続値が無いとき / 壊れ値のときの fallback.
 * 既存 `PREVIEW_THUMBNAIL_MAX_EDGE` (= 240) と同値を踏襲 (= 挙動を変えない).
 */
export const DEFAULT_PREVIEW_EDGE = PREVIEW_THUMBNAIL_MAX_EDGE;

// ─── viewport に応じた実効 maxEdge の動的 cap (= preview-size-viewport-cap) ───
//
// 背景 (alpha-feedback §8 H 要検証 / preview-resize): maxEdge は固定 160–640 で、
// 小さい画面で 640 まで広げると **窓が viewport をはみ出す**。従来は
// `clampPopupPosition` で位置を引き戻すだけでサイズ自体は縮めていなかった。
// ここで「viewport に収まる実効上限」を純関数で算出し、ドラッグリサイズの clamp
// と表示適用時の clamp で使う (= 大画面では従来どおり 640、小画面でだけ 640 未満に
// 下がる)。永続値 (= ユーザ希望サイズ) は生値のまま保持し、表示/適用時にこの実効
// max で clamp する方針 (= 大画面に戻ったら 640 が復活する)。

/**
 * プレビュー窓が viewport に収まるための安全余白 (px).
 *
 * 窓はサムネ (= 正方形換算 edge) + chrome (= title bar + padding) で構成される。
 * chrome は縦が支配的 (≈ 40px: title bar 1 行 + 上下 padding) で横は ≈ 16px。
 * さらに `clampPopupPosition` の既定マージン (8px) を画面の上下端に残したいので、
 * 縦軸の最悪ケース `edge + 40 (chrome) + 8*2 (上下マージン) <= min(vw, vh)` を満たす
 * ように `40 + 16 = 56` を安全余白とする (= min(vw, vh) - 56 を edge 上限にすれば
 * 窓の高さ edge+40 は viewport - 16 に収まる)。実機で詰める余地があるため (要検証)。
 */
export const PREVIEW_VIEWPORT_MARGIN = 56;

/**
 * viewport (window innerWidth/innerHeight) から、プレビュー窓が画面に収まる実効
 * 上限 maxEdge (px) を算出する純関数.
 *
 * `clamp(min(vw, vh) - margin, PREVIEW_MIN_EDGE, hardMax=PREVIEW_MAX_EDGE)`:
 * - viewport が大きいときは `hardMax` (= 640) に張り付く (= 従来挙動).
 * - 小さいときだけ 640 未満に下がる.
 * - どんなに小さくても `PREVIEW_MIN_EDGE` (= 160) は下回らない (= 表示範囲枠が
 *   潰れない最低サイズを保証. ここまで小さい viewport では多少はみ出すのは許容).
 *
 * `margin` / `min` / `hardMax` は引数で上書き可能 (= テスト容易性).
 */
export const effectivePreviewMaxEdge = (
  viewportWidth: number,
  viewportHeight: number,
  margin: number = PREVIEW_VIEWPORT_MARGIN,
  min: number = PREVIEW_MIN_EDGE,
  hardMax: number = PREVIEW_MAX_EDGE,
): number => {
  const avail = Math.min(viewportWidth, viewportHeight) - margin;
  return Math.min(hardMax, Math.max(min, avail));
};

/**
 * ユーザ希望 edge (= 生値) を viewport の実効 max で clamp して、実際に表示/適用
 * する edge を返す純関数.
 *
 * `clamp(edge, PREVIEW_MIN_EDGE, effectivePreviewMaxEdge(vw, vh))`. 永続値は生値の
 * まま保持し、レンダリング時にこれを通すことで「小画面では縮め、大画面に戻ったら
 * 希望サイズへ復元」を両立する (= window 縮小で現在サイズが clamp される)。
 */
export const clampPreviewEdgeToViewport = (
  edge: number,
  viewportWidth: number,
  viewportHeight: number,
  margin: number = PREVIEW_VIEWPORT_MARGIN,
): number => {
  const max = effectivePreviewMaxEdge(viewportWidth, viewportHeight, margin);
  return Math.min(max, Math.max(PREVIEW_MIN_EDGE, edge));
};

/**
 * リサイズ開始時に保存しておく "アンカー" (= origin maxEdge + pointer 開始位置).
 *
 * `onPointerDown` で `{ originEdge: cur edge, pointerStart: client coords }` を
 * 記録し、`onPointerMove` で「originEdge + (pointer delta)」を新 edge にする
 * (= ドラッグ中ジャンプしないようにする標準パターン, `FloatingResizeAnchor`
 * のスカラ版).
 */
export interface PreviewResizeAnchor {
  readonly originEdge: number;
  readonly pointerStart: { readonly x: number; readonly y: number };
}

/**
 * 右下グリップ drag から新しい maxEdge (clamp 済) を計算する純粋関数.
 *
 * 仕様 (preview-resize.md §1):
 * - `delta = max(dx, dy)` (= dx/dy の大きい方を採用. 対角ドラッグで素直に拡縮).
 * - `clamp(originEdge + delta, min, max)`.
 * - `min` / `max` は引数で上書き可能 (テスト容易性 + 将来の動的 cap 用).
 */
export const nextPreviewEdgeFromResize = (
  anchor: PreviewResizeAnchor,
  pointer: { readonly x: number; readonly y: number },
  min: number = PREVIEW_MIN_EDGE,
  max: number = PREVIEW_MAX_EDGE,
): number => {
  const delta = Math.max(pointer.x - anchor.pointerStart.x, pointer.y - anchor.pointerStart.y);
  return Math.min(max, Math.max(min, anchor.originEdge + delta));
};

// ─── 四隅グリップでのリサイズ (= H follow-up, 2026-06-11) ─────────────────
//
// 右下グリップ 1 個 (`nextPreviewEdgeFromResize`) を四隅に拡張する. アスペクトは
// canvas 比に固定したまま、「掴んだ角の対角を画面上の固定点」にして伸縮させる.
// 各角で edge を更新しつつ、対角が動かないように panel 左上 (popupPos) も同時に
// 逆算して返す pure helper. DOM 非依存 (= getBoundingClientRect 等は呼び出し側で
// 読んで `originSize` / `chrome` として渡す) で vitest node env でテストできる.
//
// | grip | 固定する角 | 動く popupPos |
// |------|-----------|---------------|
// | se   | 左上       | なし          |
// | sw   | 右上       | x             |
// | ne   | 左下       | y             |
// | nw   | 右下       | x, y          |

// ─── 枠ドラッグ (Windows 風 8 方向) リサイズ (= H follow-up #2, 2026-06-11) ───
//
// 四隅グリップ (見えるハッチ掴み代) はユーザ FB で撤去し、OS ウィンドウ風に
// 「パネル周囲の透明判定ゾーン (4 辺 + 4 隅)」をドラッグして伸縮する方式へ移行.
// pure helper も 4 隅専用 (旧 `nextPreviewCornerResize`) から 8 ゾーン
// (`nextPreviewResize`) へ一般化する. アスペクトは canvas 比に固定したまま、
// 「掴んだ辺/隅の反対側 (= 最寄りの top-left 寄り角) を画面上の固定点」にして
// 単軸 (辺) / 対角 (隅) の delta で伸縮させる.
//
// | zone   | 外向き delta      | 固定点 (= 動かさない角) | 動く popupPos |
// |--------|-------------------|-------------------------|---------------|
// | e       | dx               | 左上 (= originPos)      | なし          |
// | s       | dy               | 左上                    | なし          |
// | se      | max(dx, dy)      | 左上                    | なし          |
// | w       | -dx              | 右上                    | x             |
// | sw      | max(-dx, dy)     | 右上                    | x             |
// | n       | -dy              | 左下                    | y             |
// | ne      | max(dx, -dy)     | 左下                    | y             |
// | nw      | max(-dx, -dy)    | 右下                    | x, y          |

/** 8 方向リサイズゾーンの識別子 (4 辺 + 4 隅). */
export type PreviewResizeZone = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

/**
 * 8 方向リサイズの入力一式 (= pointerdown 時の origin + 現在の pointer).
 *
 * - `chrome` は「パネル実寸 − thumb 実寸」(= title bar + padding 分). edge に
 *   依らず一定とみなし、新 edge での thumb 寸法に足して新パネル寸法を得る.
 * - `originSize` は pointerdown 時のパネル実寸 (= `getBoundingClientRect`).
 *   固定点 (= origin の右端 / 下端) を `originPos + originSize` で得る.
 */
export interface PreviewResizeParams {
  readonly zone: PreviewResizeZone;
  readonly originEdge: number;
  readonly pointerStart: { readonly x: number; readonly y: number };
  readonly pointer: { readonly x: number; readonly y: number };
  readonly originPos: { readonly x: number; readonly y: number };
  readonly originSize: { readonly w: number; readonly h: number };
  readonly canvasWidth: number;
  readonly canvasHeight: number;
  readonly chrome: { readonly w: number; readonly h: number };
  readonly min?: number;
  readonly max?: number;
}

export interface PreviewResizeResult {
  readonly edge: number;
  readonly x: number;
  readonly y: number;
}

/**
 * 8 方向 (4 辺 + 4 隅) の枠ドラッグから新しい maxEdge + panel 左上 (popupPos)
 * を計算する純粋関数.
 *
 * 仕様 (preview-corner-resize H follow-up #2):
 * 1. `dx/dy = pointer - pointerStart`.
 * 2. ゾーンごとに「外向き = 拡大」を正にした delta を取る:
 *    辺は単軸 (e:dx / w:-dx / s:dy / n:-dy)、隅は対角の max
 *    (se:max(dx,dy) / sw:max(-dx,dy) / ne:max(dx,-dy) / nw:max(-dx,-dy)).
 * 3. `newEdge = clamp(originEdge + delta, min, max)`.
 * 4. 新 edge での thumb から新パネル寸法 (= thumb + chrome) を得る.
 * 5. 掴んだ辺/隅の反対側 (= 最寄りの top-left 寄り角) を固定点にして panel
 *    左上 (x, y) を逆算する. e/s/se は左上固定、w/sw は右上固定、n/ne は左下
 *    固定、nw は右下固定 (= 辺は同方向の隅と同じ固定角を共有).
 *
 * e/s/se は固定点が左上 = originPos なので、戻り値の (x,y) は originPos に一致.
 */
export const nextPreviewResize = (params: PreviewResizeParams): PreviewResizeResult => {
  const {
    zone,
    originEdge,
    pointerStart,
    pointer,
    originPos,
    originSize,
    canvasWidth,
    canvasHeight,
    chrome,
    min = PREVIEW_MIN_EDGE,
    max = PREVIEW_MAX_EDGE,
  } = params;

  const dx = pointer.x - pointerStart.x;
  const dy = pointer.y - pointerStart.y;

  // ゾーンごとに「外向き (= パネルが大きくなる方向)」を正とした delta.
  let delta: number;
  switch (zone) {
    case 'e':
      delta = dx;
      break;
    case 'w':
      delta = -dx;
      break;
    case 's':
      delta = dy;
      break;
    case 'n':
      delta = -dy;
      break;
    case 'se':
      delta = Math.max(dx, dy);
      break;
    case 'sw':
      delta = Math.max(-dx, dy);
      break;
    case 'ne':
      delta = Math.max(dx, -dy);
      break;
    case 'nw':
      delta = Math.max(-dx, -dy);
      break;
  }

  const edge = Math.min(max, Math.max(min, originEdge + delta));
  const thumb = computePreviewThumbnailSize(canvasWidth, canvasHeight, edge);
  const newW = thumb.width + chrome.w;
  const newH = thumb.height + chrome.h;

  // 固定する角 (origin の右端 / 下端) から新しい左上を逆算.
  const originRight = originPos.x + originSize.w;
  const originBottom = originPos.y + originSize.h;

  let x: number;
  let y: number;
  switch (zone) {
    // 左上固定 (= popupPos 不動).
    case 'e':
    case 's':
    case 'se':
      x = originPos.x;
      y = originPos.y;
      break;
    // 右上固定 (= 右端不動 → x を逆算, 上端 = originPos.y).
    case 'w':
    case 'sw':
      x = originRight - newW;
      y = originPos.y;
      break;
    // 左下固定 (= 左端 = originPos.x, 下端不動 → y を逆算).
    case 'n':
    case 'ne':
      x = originPos.x;
      y = originBottom - newH;
      break;
    // 右下固定 (= 右端 + 下端 不動).
    case 'nw':
      x = originRight - newW;
      y = originBottom - newH;
      break;
  }

  return { edge, x, y };
};

export const computePreviewThumbnailSize = (
  canvasWidth: number,
  canvasHeight: number,
  maxEdge: number = PREVIEW_THUMBNAIL_MAX_EDGE,
): PreviewThumbnailSize => {
  if (canvasWidth <= 0 || canvasHeight <= 0) {
    return { width: 1, height: 1, scale: 1 };
  }
  const longest = Math.max(canvasWidth, canvasHeight);
  const scale = maxEdge / longest;
  return {
    width: Math.max(1, Math.round(canvasWidth * scale)),
    height: Math.max(1, Math.round(canvasHeight * scale)),
    scale,
  };
};

// ─── 単発 drawImage サムネ描画の共有手続き (= Classic / Modern 共通) ───
//
// ── 変更 (feature/preview-perf 2026-06-13): 多段 mipmap + 細線ブースト撤去 ──
// 旧実装は main canvas (例 4000px) をサムネ (~168〜640px) へ「半分ずつ中間 canvas で
// 縮める多段 mipmap (computeDownscaleSteps + ping-pong scratch)」+「縮小後サムネへの
// per-pixel 細線ブースト (getImageData → LUT → putImageData の readback)」で焼いて
// いた. これを **単発 `drawImage(main, 0, 0, thumbW, thumbH)`** に置換し、ブースト
// readback も撤去した (consult 2026-06-13、launch 安全優先・最軽量). 1 回あたりの
// コストが「白 fillRect + drawImage 1 回」だけになる. サムネ上の細線が多少甘く
// なるのは launch では許容 (ユーザ確定済). 毎フレームでなく低レートスロットル
// (shouldRegenThumbnail) でしか呼ばれないので、甘さも気にならない.
//
// PreviewOverlay (floating) と ModernPreviewPanel (docked) で同じサムネ品質に
// するため、両者がこの 1 関数を呼ぶ (= 二重実装を避ける). 表示範囲枠 (黒線) の
// 重ね描きは **含めない** (= 枠はサムネ再生成と分離して安く再描画する、
// `strokePreviewViewportRect` 参照). ここは「白地に縮小画像を焼く」までを担い、
// 戻り値に「caller が ctx を整える物理寸法 + dpr」を返す.

/** `renderPreviewThumbnail` が backing store を設定したあとの物理寸法 + dpr. */
export interface PreviewThumbnailRender {
  /** backing store の物理幅 (= round(thumbWidthCss * dpr)). */
  readonly backW: number;
  /** backing store の物理高さ (= round(thumbHeightCss * dpr)). */
  readonly backH: number;
  /** 実際に使った devicePixelRatio (>0). */
  readonly dpr: number;
}

/**
 * main canvas を thumbCanvas へ **単発 drawImage** で焼き込む共有手続き.
 *
 * @param thumbCanvas 出力先サムネ canvas (backing store をここで dpr 倍に設定).
 * @param mainCanvas 縮小元の main 描画 canvas.
 * @param thumbWidthCss サムネの CSS px 幅 (= `computePreviewThumbnailSize().width`).
 * @param thumbHeightCss サムネの CSS px 高さ.
 * @param dpr devicePixelRatio (caller がサムネ生成時に読み直して渡す, >0).
 * @returns backing 物理寸法 + dpr (= 枠描画のため caller が ctx を整える材料).
 *
 * 注意:
 * - drawImage が例外 (main が一時的に 0x0 等) を投げうるので try/catch は
 *   **caller 側** で囲む (= ここでは投げっぱなし. caller は次 frame で recover).
 * - thumbCanvas の最終 ctx は `setTransform(1,0,0,1,0,0)` のまま返る. 枠を CSS px
 *   座標で描きたい caller は戻り値 dpr で `setTransform(dpr,0,0,dpr,0,0)` する.
 */
export const renderPreviewThumbnail = (
  thumbCanvas: HTMLCanvasElement,
  mainCanvas: HTMLCanvasElement,
  thumbWidthCss: number,
  thumbHeightCss: number,
  dpr: number,
): PreviewThumbnailRender => {
  const d = dpr > 0 ? dpr : 1;
  // backing store は CSS px × dpr. CSS サイズ (thumbWidthCss/HeightCss) は
  // style 側で固定し、ここでは物理ピクセル数だけを面倒みる.
  const backW = Math.max(1, Math.round(thumbWidthCss * d));
  const backH = Math.max(1, Math.round(thumbHeightCss * d));
  if (thumbCanvas.width !== backW) thumbCanvas.width = backW;
  if (thumbCanvas.height !== backH) thumbCanvas.height = backH;

  const ctx = thumbCanvas.getContext('2d');
  if (ctx === null) return { backW, backH, dpr: d };

  // ── 1. 物理ピクセル基準で透明クリア. ──
  // 背景はサムネ canvas 要素の CSS (`canvasBackgroundStyle`、メイン canvas と同じ方式) が敷く。
  // ここで白を焼くと部屋の背景色・市松が隠れる (2026-09-28 修正、旧: 白 fillRect)。
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, backW, backH);

  // ── 2. 単発 drawImage で main → サムネへ縮小. ──
  // imageSmoothing(medium) のバイリニアで 1 回だけ縮める (= 多段 mipmap 撤去).
  // 極端縮小で 1px 線がやや甘くなるが launch では許容. 低レートでしか呼ばれない.
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'medium';
  ctx.drawImage(mainCanvas, 0, 0, mainCanvas.width, mainCanvas.height, 0, 0, backW, backH);

  return { backW, backH, dpr: d };
};

/**
 * サムネ canvas 上に表示範囲枠 (黒線) だけを CSS px 座標系で描く共有手続き.
 *
 * サムネ画像の再生成 (= `renderPreviewThumbnail` の単発 drawImage) とは **分離**
 * された安い再描画経路 (feature/preview-perf 2026-06-13). スクロール/ズーム/反転が
 * 変わった瞬間に枠を追従させたいが、それでサムネ全面ダウンスケールを毎回走らせ
 * たくない. caller は「サムネ画像はスロットル/サスペンドで間引く」一方、枠だけは
 * この関数で毎フレーム安く重ね直せる (= ctx をスケールして strokeRect 1 回).
 *
 * 前提: caller がこの呼び出しの直前に **キャッシュ済みサムネ画像を thumbCanvas へ
 * blit 済み** (= 枠の下地が既にある). この関数は枠を上書き stroke するだけで、
 * サムネ画像のクリア/再描画はしない.
 *
 * @param ctx thumbCanvas の 2D context (setTransform は本関数が dpr スケールする).
 * @param thumb サムネ寸法 + scale (= `computePreviewThumbnailSize`).
 * @param rect 表示範囲矩形 (= `computePreviewViewportRect`、CSS px 座標).
 * @param dpr devicePixelRatio (物理 1px の枠線にするため lineWidth = 1/dpr).
 */
export const strokePreviewViewportRect = (
  ctx: CanvasRenderingContext2D,
  rect: PreviewViewportRect,
  dpr: number,
  /**
   * 回転中の傾いた視野枠 (`computePreviewViewportShape` の `quad`)。指定時は矩形ではなく
   * この四角形を (サムネ範囲で clip して) stroke する。
   */
  quad: PreviewViewportQuad | null = null,
): void => {
  const d = dpr > 0 ? dpr : 1;
  if (quad !== null && quad.points.length >= 3) {
    ctx.setTransform(d, 0, 0, d, 0, 0);
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 1 / d;
    ctx.save();
    // サムネからはみ出る部分は描かない (回転 0 の clamp 済み矩形と同じ見え方)。
    ctx.beginPath();
    ctx.rect(0, 0, quad.thumbWidth, quad.thumbHeight);
    ctx.clip();
    ctx.beginPath();
    quad.points.forEach((p, i) => {
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    });
    ctx.closePath();
    ctx.stroke();
    ctx.restore();
    return;
  }
  if (!(rect.w > 0 && rect.h > 0)) return;
  // CSS px 座標系で描く (= 0.5px crisp オフセットがそのまま効く).
  ctx.setTransform(d, 0, 0, d, 0, 0);
  ctx.strokeStyle = 'black';
  // 物理 1px の線にするため CSS px では 1/dpr 幅にする.
  ctx.lineWidth = 1 / d;
  // crisp な 1px 線にするため 0.5 (物理) オフセット.
  const off = 0.5 / d;
  ctx.strokeRect(rect.x + off, rect.y + off, Math.max(1, rect.w - 1), Math.max(1, rect.h - 1));
};

/**
 * 現在の表示範囲 (viewport) をサムネ座標系で表す矩形.
 *
 * 「main canvas の どの領域が今 wrapper に映っているか」をサムネのピクセル
 * に落とした (x, y, w, h). 旧版 `PreviewCanvas.paint` の
 * `drawRect(scrollH / scale, scrollV / scale, DISPLAY_W / scale / zoom,
 * DISPLAY_H / scale / zoom)` をそのまま踏襲.
 *
 * - `clamp = true` (default) でサムネ範囲内に収める. 旧版は clamp していな
 *   かったが、wrapper.clientWidth が canvas より大きいとき (= zoom 1x で
 *   ウインドウが広い場合) に枠がサムネ右下を突き抜けるので clamp する.
 */
export interface PreviewViewportRect {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

// ─── view flip (左右反転) 対応 (= CSP 流 / Option A, consult 2026-06-11) ───
//
// engine の反転表示は canvas 要素に `translateX(zoom*W) scaleX(-zoom)` を当てる
// 実装なので、同じ `tx` (viewport.ts の ViewportOffset) でも **実際に見えている
// 真の canvas 領域は左右ミラー** になる. 一方サムネ (drawImage) は真ピクセルの
// まま (= 鏡像化しない, CSP/クリスタのナビゲーター流儀). そこで表示範囲枠と
// click→viewport の **X だけ** を「thumb 幅で水平反射」して、枠を実際に見えて
// いる真の領域に一致させ、click を期待どおりの領域へ飛ばす. y/h/w は不変
// (= 水平反射のみ).
export const computePreviewViewportRect = (
  thumb: PreviewThumbnailSize,
  tx: number,
  ty: number,
  wrapperClientWidth: number,
  wrapperClientHeight: number,
  zoom: number,
  flipX = false,
): PreviewViewportRect => {
  const z = zoom <= 0 ? 1 : zoom;
  // tx/ty (= viewport.ts の ViewportOffset、表示枠 content box 左上が原点) を
  // main canvas の native px に戻すには `-tx / zoom` (画面 x=0 の真下にある
  // canvas 座標), サムネに落とすには更に `* scale`.
  // `(0 - tx)` (not `-tx`) avoids producing `-0` when `tx === 0`.
  const xMain = (0 - tx) / z;
  const yMain = (0 - ty) / z;
  // 表示幅も同様に native px に戻して scale する.
  const wMain = wrapperClientWidth / z;
  const hMain = wrapperClientHeight / z;

  let x = xMain * thumb.scale;
  let y = yMain * thumb.scale;
  let w = wMain * thumb.scale;
  let h = hMain * thumb.scale;

  // clamp: 表示範囲がサムネを突き抜けないように.
  if (x < 0) {
    w += x;
    x = 0;
  }
  if (y < 0) {
    h += y;
    y = 0;
  }
  if (x + w > thumb.width) w = thumb.width - x;
  if (y + h > thumb.height) h = thumb.height - y;
  if (w < 0) w = 0;
  if (h < 0) h = 0;

  // 反転時は clamp 済み矩形を thumb 幅で水平反射する (= 実際に見えている真の
  // canvas 領域に枠を合わせる). [0, thumb.width] 内に収まっている矩形を反射
  // しても同区間内に収まるので追加 clamp は不要.
  if (flipX) x = thumb.width - x - w;

  return { x, y, w, h };
};

/** 回転中の視野枠 (サムネ座標の 4 隅 + サムネ寸法)。 */
export interface PreviewViewportQuad {
  readonly points: readonly ViewPoint[];
  readonly thumbWidth: number;
  readonly thumbHeight: number;
}

/** 視野枠の描画データ。`quad` は回転中だけ非 null (null なら `rect` の矩形を描く)。 */
export interface PreviewViewportShape {
  readonly rect: PreviewViewportRect;
  readonly quad: PreviewViewportQuad | null;
}

/**
 * ナビ窓の視野枠 (canvas-rotation 段取り 2)。表示変換 `v` (zoom / flip / 回転 / tx / ty) と
 * 表示枠サイズから `visibleCanvasQuad` で「今映っている canvas 領域」の 4 隅を求め、サムネ座標へ
 * 落とす。
 *
 * - 回転 0: 従来どおりの clamp 済み矩形 (`computePreviewViewportRect`、flip は X 反射)。見た目は
 *   段取り 1 以前と一致する。`slotWidth/Height` は従来と同じく表示枠の clientWidth/Height。
 * - 回転中: 傾いた四角形 (`quad`、clip はサムネ範囲)。`rect` はその外接矩形 (枠が変化したかの
 *   検知キー用)。canvas 座標は flip 込みの真の座標なので X 反射は不要。
 */
export const computePreviewViewportShape = (
  thumb: PreviewThumbnailSize,
  v: ViewTransform,
  slotWidth: number,
  slotHeight: number,
): PreviewViewportShape => {
  if (v.rotation === 0) {
    return {
      rect: computePreviewViewportRect(thumb, v.tx, v.ty, slotWidth, slotHeight, v.zoom, v.flipX),
      quad: null,
    };
  }
  const points = visibleCanvasQuad(v, slotWidth, slotHeight).map((p) => ({
    x: p.x * thumb.scale,
    y: p.y * thumb.scale,
  }));
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return {
    rect: { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y },
    quad: { points, thumbWidth: thumb.width, thumbHeight: thumb.height },
  };
};

/**
 * 回転中のサムネ click/drag → 「クリック点を画面中心に置く」tx/ty (`previewClickToViewport`
 * の回転版)。サムネ座標 → 真の canvas 座標 (flip は canvas 座標側に含まれるので反射不要) →
 * `viewportCenteredOn` → 回転対応 clamp。回転 0 は従来の `previewClickToViewport` を使う。
 */
export const previewClickToViewportRotated = (
  thumb: PreviewThumbnailSize,
  clickXThumb: number,
  clickYThumb: number,
  v: ViewTransform,
  slotWidth: number,
  slotHeight: number,
): ViewportOffset => {
  const o = viewportCenteredOn(
    v,
    clickXThumb / thumb.scale,
    clickYThumb / thumb.scale,
    slotWidth,
    slotHeight,
  );
  return clampViewportForTransform({ ...v, tx: o.tx, ty: o.ty }, slotWidth, slotHeight);
};

/**
 * サムネ上の click/drag 座標 (= サムネ canvas の上端からの px) を、
 * 「クリック点が viewport の **中心** に来るような」`ViewportOffset` (tx/ty)
 * に変換する純粋関数.
 *
 * 旧版 `PreviewCanvas.mouseDragged` 踏襲: クリック点を中心に viewport 枠を
 * 移動させる. 2026-09-29 viewport transform 移行 (決定5) で戻り値を
 * `{scrollLeft,scrollTop}` から `ViewportOffset` に変更 (= `previewClickTo
 * Scroll` から改名)。clamp は `viewport.ts` の `clampViewport` (= 各辺
 * 最低 `MIN_VISIBLE_CANVAS_PX` 可視) に委譲する — 旧 `[0, maxScroll]` の
 * scroll clamp より可動域が広い (= 決定5「どこへでも動かせる」)。
 *
 * `wrapperClientWidth/Height` は表示枠の実寸 (= slot 近似、他の呼び出し箇所
 * と同じ精度) を `canvasWidth/Height` とあわせて clamp に使う.
 */
export const previewClickToViewport = (
  thumb: PreviewThumbnailSize,
  clickXThumb: number,
  clickYThumb: number,
  wrapperClientWidth: number,
  wrapperClientHeight: number,
  zoom: number,
  flipX: boolean,
  canvasWidth: number,
  canvasHeight: number,
): ViewportOffset => {
  const z = zoom <= 0 ? 1 : zoom;
  // 反転時はサムネ上のクリック X を thumb 幅で反射してから以降の式に通す
  // (= 真ピクセルのサムネ上のクリック点を、実際に見えている反転後の領域に
  // 対応する真の canvas 位置へ読み替える). Y はそのまま. これにより
  // computePreviewViewportRect(flip) の枠中心へ click すると元の位置へ戻る
  // 恒等性が成立する (枠の反射と click の反射が対称).
  const cx = flipX ? thumb.width - clickXThumb : clickXThumb;
  // クリック点 (サムネ座標) → main canvas native px.
  const xMain = cx / thumb.scale;
  const yMain = clickYThumb / thumb.scale;
  // 「クリック点を画面中心に置く」tx/ty (= viewport.ts の座標系: screenX =
  // tx + canvasX*zoom なので、screenX=中心=wrapperClientWidth/2 を解く).
  const rawTx = wrapperClientWidth / 2 - xMain * z;
  const rawTy = wrapperClientHeight / 2 - yMain * z;
  return clampViewport(
    rawTx,
    rawTy,
    canvasWidth,
    canvasHeight,
    z,
    wrapperClientWidth,
    wrapperClientHeight,
  );
};
